import { createFileRoute, Link } from '@tanstack/react-router';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/settings/api-docs/')({
  component: ApiDocsPage,
});

export { ApiDocsPage };

const ForgeCard = 'forge-card' as React.ElementType;

interface ScopeRow {
  endpoint: string;
  method: string;
  scope: string;
  header: string;
}

const SCOPE_ROWS: ScopeRow[] = [
  {
    endpoint: 'GitHub ingestion',
    method: 'POST /api/v1/integrations/ingest',
    scope: 'ingestion.write',
    header: 'Authorization: Bearer ea_…',
  },
  {
    endpoint: 'Descriptor validate',
    method: 'POST /api/v1/integrations/validate',
    scope: '*.read',
    header: 'Authorization: Bearer ea_…',
  },
  {
    endpoint: 'GraphQL reads',
    method: 'POST /graphql',
    scope: '*.read',
    header: 'Authorization: Bearer ea_…',
  },
  {
    endpoint: 'MCP server',
    method: 'POST /mcp',
    scope: '*.read (read tools)',
    header: 'Authorization: Bearer ea_…',
  },
];

const MCP_CONFIG = `{
  "mcpServers": {
    "ea-modeling": {
      "url": "https://api.modeling.ea.egov.com/mcp",
      "transport": "streamable-http",
      "headers": { "Authorization": "Bearer \${EA_API_KEY}" }
    }
  }
}`;

const CURL_EXAMPLE = `curl -X POST https://api.modeling.ea.egov.com/api/v1/integrations/ingest \\
  -H "Authorization: Bearer ea_prod_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{ "schemaVersion": "1.0.0",
        "source": { "type": "github", "repo": "org/repo", "commitSha": "abc123" },
        "descriptor": { "schemaVersion": "1.0.0",
          "service": { "id": "svc-demo", "name": "Demo" } } }'`;

function ApiDocsPage(): JSX.Element {
  return (
    <div className="api-docs-page">
      <header className="api-docs-header">
        <h1 className="forge-typography--headline4">API Documentation</h1>
        <p className="forge-typography--body1">
          Interactive documentation for the platform&apos;s integration surface. Create a key on the{' '}
          <Link to="/settings/api-keys">API Keys page</Link>, pick the scope your endpoint needs
          (see the table below), then use it as a Bearer token.
        </p>
      </header>

      <div className="api-docs-cards">
        <ForgeCard className="api-docs-card">
          <forge-icon name="api" aria-hidden="true" className="api-docs-card-icon"></forge-icon>
          <h2 className="forge-typography--title">REST — Swagger UI</h2>
          <p className="forge-typography--body2">
            GitHub ingestion write endpoints. Authorize with a key and use &quot;Try it out&quot;.
          </p>
          <Link to="/settings/api-docs/rest" className="api-docs-card-link" data-testid="link-rest">
            Open Swagger UI
            <forge-icon name="arrow_forward" aria-hidden="true"></forge-icon>
          </Link>
        </ForgeCard>

        <ForgeCard className="api-docs-card">
          <forge-icon name="hub" aria-hidden="true" className="api-docs-card-icon"></forge-icon>
          <h2 className="forge-typography--title">GraphQL — Explorer</h2>
          <p className="forge-typography--body2">
            Read-only queries with field selection. Runs as your signed-in user.
          </p>
          <Link
            to="/settings/api-docs/graphql"
            className="api-docs-card-link"
            data-testid="link-graphql"
          >
            Open GraphQL Explorer
            <forge-icon name="arrow_forward" aria-hidden="true"></forge-icon>
          </Link>
        </ForgeCard>

        <ForgeCard className="api-docs-card">
          <forge-icon
            name="smart_toy"
            aria-hidden="true"
            className="api-docs-card-icon"
          ></forge-icon>
          <h2 className="forge-typography--title">MCP Server</h2>
          <p className="forge-typography--body2">
            Connect an AI/MCP client over Streamable HTTP using an API Key.
          </p>
          <a href="#mcp" className="api-docs-card-link">
            MCP setup
            <forge-icon name="arrow_downward" aria-hidden="true"></forge-icon>
          </a>
        </ForgeCard>
      </div>

      <section className="api-docs-section" aria-labelledby="scope-heading">
        <h2 id="scope-heading" className="forge-typography--headline6">
          API Key scope reference
        </h2>
        <div className="api-docs-table-wrapper">
          <table className="api-docs-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Method / Path</th>
                <th>Required scope</th>
                <th>Auth header</th>
              </tr>
            </thead>
            <tbody>
              {SCOPE_ROWS.map((row) => (
                <tr key={row.method}>
                  <td>{row.endpoint}</td>
                  <td>
                    <code>{row.method}</code>
                  </td>
                  <td>
                    <code>{row.scope}</code>
                  </td>
                  <td>
                    <code>{row.header}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="forge-typography--body2">
          <code>admin</code> satisfies every scope check. A key missing the required scope is
          rejected with <code>403 Forbidden</code>.
        </p>
      </section>

      <section className="api-docs-section" aria-labelledby="quickstart-heading">
        <h2 id="quickstart-heading" className="forge-typography--headline6">
          Quickstart
        </h2>
        <ol className="forge-typography--body1">
          <li>
            Create a key on the <Link to="/settings/api-keys">API Keys page</Link> with the right
            scope.
          </li>
          <li>
            Copy the key when it is revealed — it is shown <strong>once</strong>.
          </li>
          <li>Send it as a Bearer token — including for MCP.</li>
        </ol>
        <pre className="api-docs-code">{CURL_EXAMPLE}</pre>
      </section>

      <section className="api-docs-section" id="mcp" aria-labelledby="mcp-heading">
        <h2 id="mcp-heading" className="forge-typography--headline6">
          Connect an MCP client
        </h2>
        <p className="forge-typography--body1">
          The MCP server speaks Streamable HTTP at <code>POST /mcp</code> and authenticates with an
          API Key sent as <code>Authorization: Bearer</code>. Read tools require the{' '}
          <code>*.read</code> scope.
        </p>
        <pre className="api-docs-code">{MCP_CONFIG}</pre>

        <h3 className="forge-typography--subtitle1">Rotating the key: config is resolved once</h3>
        <p className="forge-typography--body1">
          MCP clients (Claude Code, Claude Desktop, and similar) resolve <code>{'${VAR}'}</code>
          -style env var references in the <code>headers</code> config{' '}
          <strong>once, when the server is registered</strong> — the resolved literal token is
          written into the client&apos;s own config file. There is no live binding to the
          environment variable: changing its value afterwards (e.g. with <code>setx</code>) has no
          effect on a server that is already registered.
        </p>
        <p className="forge-typography--body1">
          API keys expire on a fixed schedule (1–365 days; see{' '}
          <code>documents/specs/api-key-lifecycle-spec.md</code>), so every MCP-connected client
          will need to be re-pointed at a new key eventually. To rotate:
        </p>
        <ol className="forge-typography--body1">
          <li>
            Create the replacement key on the <Link to="/settings/api-keys">API Keys page</Link> and
            update the environment variable it&apos;s stored in (e.g. via <code>setx</code>).
          </li>
          <li>
            Open a <strong>new</strong> terminal, or fully restart the editor for editor-integrated
            clients. An already-running shell or IDE process keeps the old value in its environment
            and will not see the update.
          </li>
          <li>
            Re-run the client&apos;s &quot;add server&quot; step from that fresh process (e.g.{' '}
            <code>claude mcp add</code> with the same server name) so the header is re-resolved with
            the new token. There is typically no separate &quot;update&quot; command — re-adding a
            server with the same name overwrites its existing registration.
          </li>
        </ol>
        <p className="forge-typography--body2">
          If an MCP client that was working suddenly fails right after a key rotation, the most
          likely cause is a stale shell/editor environment from step 2 above — the header silently
          resolves to an empty or stale token (e.g. literally <code>Authorization: Bearer</code>{' '}
          with nothing after it), not a wrong key or scope.
        </p>
        <p className="forge-typography--body2">
          See <code>documents/specs/mcp-server/</code> for the full tool catalogue and limits.
        </p>
      </section>
    </div>
  );
}
